/**
 * utils/ExcelEnricherService.js
 * Service pour l'enrichissement Excel via le backend SMART-PM avec Server-Sent Events
 */

import { smartPmBackend } from "./SmartPmBackendService.js";
import logger from "./logger.js";

class ExcelEnricherService {
  /**
   * Télécharger le fichier enrichi dans le navigateur
   * @param {string} fileId - ID du fichier généré côté backend
   * @param {string} originalFileName - Nom du fichier original
   * @returns {Promise<string>} Nom du fichier téléchargé
   */
  async downloadEnrichedFileById(fileId, originalFileName) {
    try {
      const response = await fetch(`${smartPmBackend.baseUrl}/download-enriched/${fileId}`);

      if (!response.ok) {
        throw new Error(`Erreur téléchargement: HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // Extraire le nom du fichier de la réponse si disponible
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `${originalFileName.replace(/\.[^/.]+$/, "")}_enrichi_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Télécharger le fichier
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return filename;
    } catch (error) {
      logger.error(
        {
          context: "download-enriched-file-by-id",
          error: error.message,
          fileId,
        },
        "Erreur téléchargement fichier enrichi"
      );
      throw new Error(`Erreur téléchargement: ${error.message}`);
    }
  }

  /**
   * Processus complet d'enrichissement avec SSE pour feedback temps réel
   * @param {Object} backendData - Données du backend contenant toutes les infos nécessaires
   * @param {Function} onProgress - Callback pour les mises à jour de progression
   * @returns {Promise<Object>} Résultat de l'enrichissement
   */
  async enrichBaseRop(backendData, onProgress = null) {
    try {
      if (onProgress) onProgress("Vérification du service...");

      const health = await smartPmBackend.checkHealth();
      if (!health.healthy) {
        throw new Error("Service d'enrichissement Excel indisponible. Vérifiez que le service backend est démarré.");
      }

      if (onProgress) onProgress("Préparation des données enrichies...");

      // Validation des données enrichies
      if (!backendData?.enrichedFibers || !Array.isArray(backendData.enrichedFibers)) {
        throw new Error("Données enrichies manquantes ou invalides");
      }

      if (backendData.enrichedFibers.length === 0) {
        throw new Error("Aucune fibre enrichie à traiter");
      }

      // Validation du fichier Excel original
      if (!backendData?.originalFile || !(backendData.originalFile instanceof File)) {
        throw new Error("Fichier Excel original non trouvé. Veuillez recharger le fichier.");
      }

      // Validation des métadonnées Excel
      if (!backendData?.referenceColumn?.letter || !backendData?.portColumn?.letter) {
        throw new Error("Informations des colonnes manquantes dans backendData");
      }

      if (!backendData?.worksheetName) {
        throw new Error("Nom de la feuille Excel manquant");
      }

      if (!backendData?.bpoColumn?.letter) {
        throw new Error("Colonne BPO manquante");
      }

      if (onProgress) onProgress("Connexion au service d'enrichissement...");

      // Préparer le FormData
      const formData = new FormData();
      formData.append("excel_file", backendData.originalFile);
      formData.append("sheet_name", backendData.worksheetName);
      formData.append("reference_column_letter", backendData.referenceColumn.letter);
      formData.append("port_column_letter", backendData.portColumn.letter);
      formData.append("bpo_column_letter", backendData.bpoColumn.letter);
      formData.append("status_column_name", "STATUS_ITE");
      formData.append("distance_column_name", "DISTANCE_ITE");
      formData.append("comment_column_name", "COMMENT_ITE");
      formData.append("enriched_data", JSON.stringify(backendData.enrichedFibers));

      logger.info(
        {
          context: "excel-enrichment-sse-request",
          fileName: backendData.originalFile.name,
          sheetName: backendData.worksheetName,
          enrichedFibersCount: backendData.enrichedFibers.length,
        },
        "Envoi requête enrichissement SSE"
      );

      // Créer une promesse pour gérer le résultat SSE
      const result = await new Promise((resolve, reject) => {
        // Créer la requête fetch pour initier le SSE
        fetch(`${smartPmBackend.baseUrl}/enrich-excel-stream`, {
          method: "POST",
          body: formData,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Erreur HTTP ${response.status}`);
            }

            // Créer EventSource à partir du stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            const processStream = async () => {
              try {
                while (true) {
                  const { done, value } = await reader.read();

                  if (done) {
                    break;
                  }

                  buffer += decoder.decode(value, { stream: true });

                  // Traiter les événements complets dans le buffer
                  const events = buffer.split("\n\n");
                  buffer = events.pop() || ""; // Garder le dernier fragment incomplet

                  for (const eventText of events) {
                    if (!eventText.trim()) continue;
                    try {
                      const lines = eventText.split("\n");
                      const eventType = lines[0]?.replace("event: ", "").trim();
                      const dataLine = lines[1]?.replace("data: ", "").trim();

                      if (!dataLine) continue;

                      const data = JSON.parse(dataLine);

                      // Gérer différents types d'événements
                      if (eventType === "start") {
                        if (onProgress) onProgress(data.message);
                      } else if (eventType === "progress") {
                        if (onProgress) {
                          const progressMsg = data.percentage ? `${data.message} (${data.percentage}%)` : data.message;
                          onProgress(progressMsg);
                        }
                      } else if (eventType === "complete") {
                        // Enrichissement terminé avec succès
                        if (onProgress) onProgress("Téléchargement du fichier...");

                        // Télécharger le fichier
                        this.downloadEnrichedFileById(data.fileId, backendData.originalFile.name)
                          .then((downloadedFileName) => {
                            resolve({
                              success: true,
                              fileName: downloadedFileName,
                              fileSize: "N/A", // Pas disponible en SSE
                              enrichedFibers: backendData.enrichedFibers.length,
                              timestamp: data.timestamp || new Date().toISOString(),
                              version: "v2.0-sse",
                              stats: data.stats,
                            });
                          })
                          .catch((error) => {
                            reject(new Error(`Erreur téléchargement: ${error.message}`));
                          });
                      } else if (eventType === "error") {
                        reject(new Error(data.message || "Erreur inconnue"));
                      }
                    } catch (parseError) {
                      logger.warn(
                        {
                          context: "sse-event-parse",
                          error: parseError.message,
                          eventText: eventText.substring(0, 100),
                        },
                        "Erreur parsing événement SSE"
                      );
                    }
                  }
                }
              } catch (streamError) {
                reject(streamError);
              }
            };

            processStream();
          })
          .catch((fetchError) => {
            reject(new Error(`Erreur connexion: ${fetchError.message}`));
          });
      });

      logger.info(
        {
          context: "enrichment-success",
          fileName: result.fileName,
          enrichedFibersCount: result.enrichedFibers,
        },
        "Enrichissement terminé avec succès"
      );

      return result;
    } catch (error) {
      logger.error(
        {
          context: "enrichment-process",
          enrichedFibersCount: backendData?.enrichedFibers?.length || 0,
          hasOriginalFile: !!backendData?.originalFile,
          worksheetName: backendData?.worksheetName,
          error: error.message,
          stack: error.stack,
        },
        "Erreur processus enrichissement"
      );
      throw error;
    }
  }
}

export const excelEnricherService = new ExcelEnricherService();
